const VIDEO_ID_REGEX = /^[a-zA-Z0-9_-]{11}$/;

/**
 * Extract the 11-character video ID from various YouTube URL formats.
 * Returns null if the URL is not a recognized YouTube URL or has no valid ID.
 */
export function extractYouTubeVideoId(url: string): string | null {
  const trimmed = url.trim();
  if (trimmed === '') return null;

  try {
    const parsed = new URL(trimmed);

    // youtube.com/watch?v=ID or youtube.com/embed/ID
    if (parsed.hostname === 'www.youtube.com' || parsed.hostname === 'youtube.com' || parsed.hostname === 'm.youtube.com') {
      const vParam = parsed.searchParams.get('v');
      if (vParam !== null && VIDEO_ID_REGEX.test(vParam)) {
        return vParam;
      }

      const embedMatch = parsed.pathname.match(/^\/embed\/([a-zA-Z0-9_-]{11})/);
      if (embedMatch !== null) {
        return embedMatch[1];
      }

      const liveMatch = parsed.pathname.match(/^\/live\/([a-zA-Z0-9_-]{11})/);
      if (liveMatch !== null) {
        return liveMatch[1];
      }

      return null;
    }

    // youtu.be/ID
    if (parsed.hostname === 'youtu.be') {
      const id = parsed.pathname.slice(1); // remove leading /
      if (VIDEO_ID_REGEX.test(id)) {
        return id;
      }
      return null;
    }

    return null;
  } catch {
    return null;
  }
}

/**
 * Clean and normalize a YouTube URL to the canonical format: https://www.youtube.com/watch?v=ID
 * Returns null if the input is not a valid YouTube URL.
 * Returns '' if the input is empty (empty is valid = no stream).
 */
export function cleanYouTubeUrl(input: string): string | null {
  const trimmed = input.trim();
  if (trimmed === '') return '';

  const videoId = extractYouTubeVideoId(trimmed);
  if (videoId === null) return null;

  return `https://www.youtube.com/watch?v=${videoId}`;
}

/**
 * Check if a string is a valid YouTube URL (or empty).
 */
export function isValidYouTubeUrl(input: string): boolean {
  return cleanYouTubeUrl(input) !== null;
}
